/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { fetchAppConfig } from '../../store/actions/authActionCreator';
import {
    Button,
    Paper,
    Box,
    Typography,
    makeStyles,
    TablePagination,
} from '@material-ui/core';
import Searchbar from '../../components/Searchbar';
import { Spinner } from '../../components/UI';
import { getActiveInitRoute, getPermissions } from '../../utils/helper';
import { EmptyIcon } from '../../assets/svg';
import FilterModal, {
    FilteredState,
    FILTER_INPUT_TYPE,
} from '../../components/Modals/FilterModal';
import { GetCities } from '../../store/actions/settingsActionCreator';
import moment from 'moment';
import { useDebounce } from '../../hooks/useDebounce';
import {
    fetchAgencyDashboardCounts,
    fetchAgencyLeadList,
    uploadAgencySingleLead,
    uploadAgencyBulkLeads,
    fetchAgencyLookup,
    fetchAgencyDailyLimit,
} from '../../store/actions/agencyActionCreator';
import { fetchAgencyHubList } from '../../store/actions/agencyActionCreator';
import AgencyHireItem from './AgencyHireItem';
import AgencySingleUploadModal from './AgencySingleUploadModal';
import AgencyBulkUploadModal from './AgencyBulkUploadModal';
import BulkUploadHistoryModal from './BulkUploadHistoryModal';

const useStyles = makeStyles(() => ({
    cardSubHeader: {
        fontSize: '22px',
        fontWeight: 700,
    },
    cardHeader: {
        fontSize: '13px',
        fontWeight: 600,
        lineHeight: 1.3,
    },
    verticalCardContents: {
        flex: '1 1 0',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        justifyContent: 'center',
        border: 'solid 1px #ebedf8',
        borderRadius: '3.8px',
        backgroundColor: '#ebedf8',
        paddingLeft: '12px',
        height: '88px',
        margin: '0 6px 0 0',
        cursor: 'pointer',
    },
    verticalCardContentsActive: {
        backgroundColor: '#d1d8ff',
    },
    verticalCardContentsNoMargin: {
        margin: 0,
    },
}));

const AgencyStages = [
    { label: 'Lead Uploaded',    key: 'leadUploaded',   value: 'LEAD_UPLOADED' },
    { label: 'App Downloaded',   key: 'appDownloaded',  value: 'APP_DOWNLOADED' },
    { label: 'KYC Verified',     key: 'kycVerified',    value: 'KYC_VERIFIED' },
    { label: 'Joining Fee Paid', key: 'joiningFeePaid', value: 'JOINING_FEE_PAID' },
    { label: 'ID Created',       key: 'idCreated',      value: 'ID_CREATED' },
    { label: 'Store Visit Done', key: 'storeVisitDone', value: 'STORE_VISIT_DONE' },
    { label: 'FOD Done',         key: 'fodDone',        value: 'FOD_DONE' },
];

const referralStatuses = [
    { label: 'All',         key: 'all',        value: null },
    { label: 'KYC Pending', key: 'kycPending', value: 'KYC_PENDING' },
    { label: 'Converted',   key: 'converted',  value: 'CONVERTED' },
    { label: 'Matured',     key: 'matured',    value: 'MATURED' },
    { label: 'Expired',     key: 'expired',    value: 'EXPIRED' },
];
 
const toOptions = (items) => (items ?? []).map((i) => ({ label: i.name, value: i.id }));

// Single source for the API filter fields, used by apply, chip delete and counts
const filterPayload = (values) => ({
    startDate: values.startDate,
    endDate: values.endDate,
    stage: values.subStatusOption?.value ?? null,
    referralStatus: values.referralStatusOption?.value ?? null,
    cityId: values.cityOption?.value ?? null,
    zoneId: values.zoneOption?.value ?? null,
    storeId: values.storeOption?.value ?? null,
});

const defaultDateRange = {
    leadDate: {
        startDate: moment().subtract(6, 'days').startOf('day').valueOf(),
        endDate: moment().endOf('day').valueOf(),
    },
    startDate: moment().subtract(6, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
};

const AgencyHire = ({
    user,
    cityList,
    hubList,
    configs,
    dispatchFetchDashboardCounts,
    dispatchFetchLeadList,
    dispatchUploadSingleLead,
    dispatchUploadBulkLeads,
    dispatchGetCities,
    dispatchFetchLookup,
    dispatchFetchDailyLimit,
    dispatchFetchAppConfig,
    dispatchGetHubList,
    ...props
}) => {
    const classes = useStyles();
    const { debounce } = useDebounce();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(true);
    const [countLoading, setCountLoading] = useState(false);
    const [showCounts, setShowCounts] = useState(false);
    const [leadCounts, setLeadCounts] = useState({});
    const [lineItems, setLineItems] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [searchVal, setSearchVal] = useState('');
    const [permissions, setPermissions] = useState(getPermissions('agency_hire'));
    const [activeStage, setActiveStage] = useState(null);
    const [filteredValues, setFilteredValues] = useState({ ...defaultDateRange });
    const [listingPayload, setListingPayload] = useState({
        ...defaultDateRange,
        stage: null,
        pageNo: 1,
        pageSize: 10,
        searchText: null,
    });
    const [singleUploadOpen, setSingleUploadOpen] = useState(false);
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
    const [uploadHistoryOpen, setUploadHistoryOpen] = useState(false);
    const [filterZones, setFilterZones] = useState([]);
    const [filterStores, setFilterStores] = useState([]);
    const [dailyLimit, setDailyLimit] = useState(null);

    const fetchDailyLimit = () => dispatchFetchDailyLimit((resp) => setDailyLimit(resp?.response ?? null));

    useEffect(() => {
        if (!permissions.read) {
            props.history.push(`/${getActiveInitRoute()}`);
        }
        dispatchGetCities();
        fetchDailyLimit();
        dispatchFetchAppConfig();
        dispatchGetHubList();
        // Counts are loaded lazily — only when user clicks "Show Counts"
    }, []);

    useEffect(() => {
        setPermissions(getPermissions('agency_hire'));
        if (!getPermissions('agency_hire').read) {
            props.history.push(`/${getActiveInitRoute()}`);
        }
    }, [configs]);

    // Zone and Store filter options follow the City picked in the filter modal
    const filterCityId = filteredValues.cityOption?.value;
    useEffect(() => {
        let active = true; // ignore replies for a city the user already moved away from
        setFilterZones([]);
        setFilterStores([]);
        if (filterCityId) {
            dispatchFetchLookup('zones', filterCityId, (resp) => active && setFilterZones(resp?.response?.items ?? []));
        }
        dispatchFetchLookup('stores', filterCityId, (resp) => active && setFilterStores(resp?.response?.items ?? []));
        return () => {
            active = false;
        };
    }, [filterCityId]);

    // Tiles are grouped by stage, so every filter except stage applies to them
    const fetchCounts = (payload = listingPayload) => {
        setCountLoading(true);
        dispatchFetchDashboardCounts(
            {
                startDate: payload.startDate,
                endDate: payload.endDate,
                referralStatus: payload.referralStatus ?? null,
                cityId: payload.cityId ?? null,
                zoneId: payload.zoneId ?? null,
                storeId: payload.storeId ?? null,
            },
            (resp) => {
                setCountLoading(false);
                setLeadCounts(resp?.response ?? {});
            },
            () => setCountLoading(false),
        );
    };

    const fetchLeads = (payload = {}) => {
        setLoading(true);
        // Strip UI-only fields before sending to API
        const { leadDate, ...apiPayload } = payload;
        dispatchFetchLeadList(
            apiPayload,
            (resp) => {
                setLoading(false);
                setLineItems(resp?.response?.leads ?? []);
                setHasMore(resp?.response?.hasMore ?? false);
            },
            () => setLoading(false),
        );
    };

    useEffect(() => {
        fetchLeads(listingPayload);
    }, [listingPayload]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        setListingPayload((prev) => ({ ...prev, pageNo: newPage + 1 }));
    };

    const handleChangeRowsPerPage = (event) => {
        const size = +event.target.value;
        setRowsPerPage(size);
        setPage(0);
        setListingPayload((prev) => ({ ...prev, pageNo: 1, pageSize: size }));
    };

    const onFilterApplied = () => {
        setPage(0);
        const filters = filterPayload(filteredValues);
        setActiveStage(filters.stage);
        const newPayload = {
            ...listingPayload,
            ...filters,
            pageNo: 1,
        };
        setListingPayload(newPayload);
        // Re-fetch counts with new date range if counts panel is open
        if (showCounts) fetchCounts(newPayload);
    };

    // A single-select filter stored as `name` (chip label) + `${name}Option` (the picked {label, value})
    const selectFilter = (values, label, name, options, alsoClear = []) => ({
        type: FILTER_INPUT_TYPE.SELECT,
        label,
        name,
        value: values?.[`${name}Option`] ?? null,
        options,
        keysToDelete: [name, `${name}Option`, ...alsoClear],
        onChange: (e) => {
            const next = { ...values };
            alsoClear.forEach((k) => delete next[k]);
            setFilteredValues({
                ...next,
                [name]: e?.target?.value?.label ?? null,
                [`${name}Option`]: e?.target?.value ?? null,
            });
        },
    });

    const prepareFilterOptions = (values) => [
        {
            type: FILTER_INPUT_TYPE.DATERANGE,
            label: 'Lead Date',
            name: 'leadDate',
            value: values?.leadDate ?? undefined,
            onChange: (e) => {
                setFilteredValues({
                    ...values,
                    leadDate: e,
                    startDate: moment(e.startDate).format('YYYY-MM-DD'),
                    endDate: moment(e.endDate).format('YYYY-MM-DD'),
                });
            },
            render: () => {
                if (
                    moment(values?.leadDate?.startDate)?.valueOf() ===
                        moment().subtract(6, 'days').startOf('day')?.valueOf() &&
                    moment(values?.leadDate?.endDate)?.valueOf() ===
                        moment().endOf('day')?.valueOf()
                )
                    return `Date: Last 7 Days`;
                return `Date: ${values.startDate} - ${values.endDate}`;
            },
        },
        {
            type: FILTER_INPUT_TYPE.SELECT,
            label: 'Lead Stage',
            name: 'subStatus',
            value: values?.subStatusOption ?? null,
            options: AgencyStages.map((s) => ({ label: s.label, value: s.value, ...s })),
            keysToDelete: ['subStatus', 'subStatusOption'],
            onChange: (e) => {
                setFilteredValues({
                    ...values,
                    subStatus: e?.target?.value?.label ?? null,
                    subStatusOption: e?.target?.value ?? null,
                });
            },
        },
        {
            type: FILTER_INPUT_TYPE.SELECT,
            label: 'Referral Status',
            name: 'referralStatus',
            value: values?.referralStatusOption ?? null,
            options: referralStatuses.filter((s) => s.value !== null).map((s) => ({ label: s.label, value: s.value, ...s })),
            keysToDelete: ['referralStatus', 'referralStatusOption'],
            onChange: (e) => {
                setFilteredValues({
                    ...values,
                    referralStatus: e?.target?.value?.label ?? null,
                    referralStatusOption: e?.target?.value ?? null,
                });
            },
        },
        // Zone and Store belong to a city, so picking a City clears them
        selectFilter(values, 'City', 'city', toOptions(cityList), ['zone', 'zoneOption', 'store', 'storeOption']),
        selectFilter(values, 'Zone', 'zone', toOptions(filterZones)),
        selectFilter(values, 'Store', 'store', toOptions(filterStores)),
    ];

    return (
        <div>
            <Spinner loading={countLoading}>
                <Paper className="cardStyles bottom-mar" elevation={0}>
                    <Box className="horizontalCardContents" style={{ flexWrap: 'nowrap' }}>
                        {AgencyStages.map((stage, index) => (
                            <Box
                                key={stage.value}
                                className={`${classes.verticalCardContents} ${
                                    activeStage === stage.value
                                        ? classes.verticalCardContentsActive
                                        : ''
                                } ${
                                    index === AgencyStages.length - 1
                                        ? classes.verticalCardContentsNoMargin
                                        : ''
                                }`}
                                onClick={() => {
                                    const next =
                                        activeStage === stage.value
                                            ? null
                                            : stage.value;
                                    setActiveStage(next);
                                    setPage(0);
                                    setFilteredValues((prev) => ({
                                        ...prev,
                                        subStatus: next ? stage.label : null,
                                        subStatusOption: next ? stage : null,
                                    }));
                                    setListingPayload((prev) => ({
                                        ...prev,
                                        stage: next,
                                        pageNo: 1,
                                    }));
                                }}
                            >
                                <Typography className={classes.cardHeader}>
                                    {stage.label}
                                </Typography>
                                <Typography className={classes.cardSubHeader}>
                                    {showCounts ? (leadCounts?.[stage.key] ?? 0) : '—'}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Paper>
            </Spinner>

            <Spinner loading={loading}>
                <Paper style={{ padding: '12px 0' }} elevation={0}>
                    <Box
                        className="horiz-end"
                        style={{ margin: '24px 16px 16px 24px' }}
                    >
                        <Box
                            style={{
                                width: '400px',
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Searchbar
                                placeHolderText="Search by Name or Contact No."
                                backendSearchbar={true}
                                onBackendSearch={(searchText) => {
                                    setPage(0);
                                    setSearchVal(searchText);
                                }}
                                value={searchVal}
                                keys={['name', 'contactNumber']}
                                onFilter={() => {}}
                                onChange={(value) => setSearchVal(value)}
                            />
                            <Button
                                style={{ marginLeft: 8 }}
                                variant="outlined"
                                color="primary"
                                onClick={() => {
                                    const isReset =
                                        !!listingPayload?.searchText &&
                                        listingPayload?.searchText === searchVal;
                                    setListingPayload((prev) => ({
                                        ...prev,
                                        pageNo: 1,
                                        searchText: isReset ? null : searchVal || null,
                                    }));
                                    if (isReset) setSearchVal('');
                                }}
                                disabled={!searchVal}
                            >
                                {!!listingPayload?.searchText &&
                                listingPayload?.searchText === searchVal
                                    ? 'Reset'
                                    : 'Search'}
                            </Button>
                            <FilterModal
                                filterOptions={prepareFilterOptions(filteredValues)}
                                filteredValues={filteredValues}
                                onFilterApplied={onFilterApplied}
                            />
                        </Box>

                        <Box>
                            <Button
                                style={{ marginRight: 8 }}
                                variant="outlined"
                                color="primary"
                                onClick={() => {
                                    if (!showCounts) {
                                        fetchCounts(listingPayload);
                                    }
                                    setShowCounts((prev) => !prev);
                                }}
                            >
                                {showCounts ? 'Hide Counts' : 'Show Counts'}
                            </Button>
                            {configs?.agencyHireBulkUploadEnabled !== false && (
                                <>
                                    <Button
                                        style={{ marginRight: 8 }}
                                        variant="outlined"
                                        color="primary"
                                        disableElevation
                                        onClick={() =>
                                            setUploadHistoryOpen(true)
                                        }
                                    >
                                        Upload History
                                    </Button>
                                    {dailyLimit?.remaining != null && dailyLimit?.dailyLimit != null && (
                                        <Typography
                                            component="span"
                                            style={{ marginRight: 8, fontSize: '13px', color: '#555' }}
                                        >
                                            {dailyLimit.remaining.toLocaleString('en-IN')} of{' '}
                                            {dailyLimit.dailyLimit.toLocaleString('en-IN')} left today
                                        </Typography>
                                    )}
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className="btn-pos"
                                        disableElevation
                                        onClick={() => setBulkUploadOpen(true)}
                                    >
                                        Bulk Upload
                                    </Button>
                                </>
                            )}
                            <Button
                                variant="contained"
                                color="primary"
                                className="btn-pos"
                                disableElevation
                                onClick={() => {
                                    dispatchGetHubList();
                                    setSingleUploadOpen(true);
                                }}
                            >
                                Add Lead
                            </Button>
                        </Box>
                    </Box>

                    <FilteredState
                        filterOptions={prepareFilterOptions(filteredValues)}
                        filteredValues={filteredValues}
                        onDelete={(itemKey, item) => {
                            let next = { ...filteredValues };
                            if (item?.keysToDelete?.length > 0) {
                                item.keysToDelete.forEach((k) => delete next[k]);
                            } else {
                                delete next[itemKey];
                            }
                            if (itemKey === 'leadDate') {
                                delete next.startDate;
                                delete next.endDate;
                            }
                            setFilteredValues(next);
                            const filters = filterPayload(next);
                            setActiveStage(filters.stage);
                            const nextPayload = {
                                ...listingPayload,
                                ...filters,
                                pageNo: 1,
                            };
                            setListingPayload(nextPayload);
                            setPage(0);
                            if (showCounts) fetchCounts(nextPayload);
                        }}
                    />

                    {lineItems.length === 0 ? (
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <EmptyIcon />
                        </Box>
                    ) : (
                        <Box style={{ margin: '0 24px 0 24px' }}>
                            <AgencyHireItem data={lineItems} />
                            <TablePagination
                                rowsPerPageOptions={[10, 50, 100]}
                                component="div"
                                count={
                                    hasMore
                                        ? (page + 2) * rowsPerPage
                                        : page * rowsPerPage + lineItems.length
                                }
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onChangePage={handleChangePage}
                                onChangeRowsPerPage={handleChangeRowsPerPage}
                                labelDisplayedRows={({ from, to }) => `${from}-${to}`}
                            />
                        </Box>
                    )}
                </Paper>
            </Spinner>

            {singleUploadOpen && (
                <AgencySingleUploadModal
                    open={singleUploadOpen}
                    onClose={() => setSingleUploadOpen(false)}
                    dispatchUploadSingleLead={dispatchUploadSingleLead}
                    hubList={hubList}
                    cityList={cityList}
                    dispatchFetchLookup={dispatchFetchLookup}
                    onSuccess={() => {
                        setSingleUploadOpen(false);
                        setPage(0);
                        setListingPayload((prev) => ({ ...prev, pageNo: 1 }));
                        fetchCounts();
                    }}
                />
            )}

            {bulkUploadOpen && (
                <AgencyBulkUploadModal
                    open={bulkUploadOpen}
                    onClose={() => setBulkUploadOpen(false)}
                    dispatchUploadBulkLeads={dispatchUploadBulkLeads}
                    onSuccess={() => {
                        setBulkUploadOpen(false);
                        setPage(0);
                        setListingPayload((prev) => ({ ...prev, pageNo: 1 }));
                        fetchCounts();
                        fetchDailyLimit();
                        setUploadHistoryOpen(true);
                    }}
                />
            )}

            {uploadHistoryOpen && (
                <BulkUploadHistoryModal
                    open={uploadHistoryOpen}
                    onClose={() => setUploadHistoryOpen(false)}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    cityList: state.settings?.cities ?? [],
    hubList: state.staff?.hubs ?? [],
    user: state.user,
    configs: state.user.config,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchFetchDashboardCounts: (data, onSuccess, onError) =>
        dispatch(fetchAgencyDashboardCounts(data, onSuccess, onError)),
    dispatchFetchLeadList: (data, onSuccess, onError) =>
        dispatch(fetchAgencyLeadList(data, onSuccess, onError)),
    dispatchUploadSingleLead: (data, onSuccess, onError) =>
        dispatch(uploadAgencySingleLead(data, onSuccess, onError)),
    dispatchUploadBulkLeads: (data, onSuccess, onError) =>
        dispatch(uploadAgencyBulkLeads(data, onSuccess, onError)),
    dispatchGetCities: () => dispatch(GetCities()),
    dispatchFetchDailyLimit: (onSuccess, onError) => dispatch(fetchAgencyDailyLimit(onSuccess, onError)),
    dispatchFetchLookup: (type, cityId, onSuccess, onError) =>
        dispatch(fetchAgencyLookup(type, cityId, onSuccess, onError)),
    dispatchGetHubList: (onSuccess, onError) =>
        dispatch(fetchAgencyHubList(onSuccess, onError)),
    dispatchFetchAppConfig: (onSuccess, onError) =>
        dispatch(fetchAppConfig(onSuccess, onError)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AgencyHire);
